<!-- TextCardsForm.vue
     Mesma tela do TextForm.vue — filtros, busca, ações em lote, paginação e os
     mesmos modais —, trocando a TABELA por uma grade de cards.

     É uma CÓPIA do TextForm, não uma refatoração dele: a tela de tabela continua
     em produção e não pode ser alterada, e extrair a lógica para um composable
     mexeria justamente nela. O preço é a duplicação, e ele é conhecido: correção
     de comportamento feita num arquivo precisa ser repetida no outro.

     O que existe aqui e não lá: a capa desenhada no cliente e o botão "Acervo
     Litterae". -->
<script setup>
import { useRouter } from "vue-router";
import { ref, reactive, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import debounce from "lodash.debounce";
import { toTitleCase } from "@/utils/stringUtils";
import { catalogChips, catalogLabel } from "@/utils/textCatalog";
import { coverStyle, coverTombo } from "@/utils/textCover";
import api from "@/services/api";
// eslint-disable-next-line no-unused-vars
import { formatDateTime, formatDateTimeForDisplay } from "@/utils/dateUtils";

// Stores
import { useTextStore } from "@/stores/useTextStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useAuthorStore } from "@/stores/useAuthorStore";
import { useCategoryStore } from "@/stores/useCategoryStore";
import { useTopicStore } from "@/stores/useTopicStore";
import { useUserStore } from "@/stores/useUserStore";
// ── NOVO: guard de permissão por vínculo Membro-Titular ───────────────────────
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";

// Componentes modais
import AddTextForm from "./AddTextForm.vue";
import EditTextForm from "./EditTextForm.vue";
import DeleteTextForm from "./DeleteTextForm.vue";
import BatchDeleteTextForm from "./BatchDeleteTextForm.vue";
import TextCardModal from "./TextCardModal.vue";
import TextHelpModal from "./TextHelpModal.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";

const showHelpModal = ref(false);

// Componentes de paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

const router = useRouter();

// Stores
const textStore = useTextStore();
const activityStore = useActivityStore();
const authorStore = useAuthorStore();
const categoryStore = useCategoryStore();
const userStore = useUserStore();
const topicStore = useTopicStore();
// ── NOVO ──────────────────────────────────────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

// ── NOVO: permissões efetivas do vínculo (null = sem restrição) ───────────────
const memberTextPermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.texts ?? null
);

const memberCanViewTexts = computed(
  () => memberTextPermissions.value === null || memberTextPermissions.value.view
);
// Criar/editar/excluir combinam o grid do vínculo com a opção "somente conteúdo
// do titular" (que bloqueia toda escrita de texto pelo membro).
const {
  canCreateText: memberCanCreateText,
  canEditText: memberCanEditText,
  canDeleteText: memberCanDeleteText,
} = useMemberRestrictions();


// ── NOVO: guard de atividades — portão para o botão "Atividades" na linha ─────
const memberActivityPermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.activities ?? null
);
const memberCanViewActivities = computed(
  () =>
    memberActivityPermissions.value === null ||
    memberActivityPermissions.value.view
);

// 🔹 Campos padrão da busca
const DEFAULT_TEXT_SEARCH_FIELDS = [
  "title",
  "subtitle",
  "author",
  "category",
  "topics",
  "creator",
  "source",
];

function resetTextSearchState() {
  textStore.searchTerm = "";
  textStore.textSearchFields = [...DEFAULT_TEXT_SEARCH_FIELDS];
  textStore.currentPage = 1;
  textStore.shouldForcePageOne = false;
  textStore.setMyTextsOnly(false);
  textStore.clearFilters();
}

// --- LÓGICA DO TOAST ---
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 6000);
}

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showTextCardModal = ref(false);
const selectedTextCardId = ref(null);

const showPermissionModal = ref(false);
const selectedPermissionTextId = ref(null);

// Paginação
const currentPage = ref(1);
// 24 cards por página — em 3 colunas, oito fileiras cheias, sem card solto na
// última. Ref local, como no TextForm: a tabela continua com o pageSize dela.
const pageSize = ref(24);
const isLoading = ref(false);

// Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = textStore.texts
      .filter((t) => canEdit(t))
      .map((t) => t.textId);
    if (editableIdsOnPage.length === 0) return false;
    return editableIdsOnPage.every((id) =>
      textStore.selectedTextIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = textStore.texts
      .filter((t) => canEdit(t))
      .map((t) => t.textId);
    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!textStore.selectedTextIds.includes(id))
          textStore.selectedTextIds.push(id);
      });
    } else {
      textStore.selectedTextIds = textStore.selectedTextIds.filter(
        (id) => !editableIdsOnPage.includes(id)
      );
    }
  },
});

const anyEditableOnPage = computed(() =>
  textStore.texts.some((t) => canEdit(t))
);

const canBatchActions = computed(
  () =>
    Array.isArray(textStore.selectedTextIds) &&
    textStore.selectedTextIds.length > 0
);

// ── Posicionamento do menu de ações da linha ────────────────────────────────
//
// O menu é `position: absolute` ancorado na célula, e não teleportado: assim ele
// acompanha a rolagem e nunca se descola da linha. O que faltava era escolher o
// LADO. Aberto sempre para baixo, o menu das últimas linhas passa da borda do
// `.table-wrapper` — que recorta, porque `overflow-x: auto` faz o eixo Y computar
// `auto` — e o fim dele fica cortado.
//
// Mesma solução já em uso no ActivityForm; as classes `opens-up`/`opens-left`
// espelham a ancoragem.
const MENU_EDGE_MARGIN = 8;
const MENU_MIN_HEIGHT = 120;
const ROW_MENU_OFFSET = 42; // igual ao left: 42px de .row-dropdown-menu

const rowMenuPlacement = ref({
  up: false,
  left: false,
  maxHeight: null,
  ready: false,
});

// Quem recorta é o `.table-wrapper`. A janela entra na conta para o caso de o
// wrapper se estender além dela.
function getClipBounds(menuEl) {
  const wrapper = menuEl.closest(".table-wrapper");
  const r = wrapper?.getBoundingClientRect();
  return {
    top: Math.max(r?.top ?? 0, 0) + MENU_EDGE_MARGIN,
    bottom:
      Math.min(r?.bottom ?? window.innerHeight, window.innerHeight) -
      MENU_EDGE_MARGIN,
    left: Math.max(r?.left ?? 0, 0) + MENU_EDGE_MARGIN,
    right:
      Math.min(r?.right ?? window.innerWidth, window.innerWidth) -
      MENU_EDGE_MARGIN,
  };
}

// A altura real só existe depois de o `v-if` renderizar o menu, daí o nextTick.
// Até a medição ele fica com `visibility: hidden`, para não dar um salto visível
// quando a conta decidir abrir para o outro lado.
async function placeRowMenu() {
  await nextTick();
  const menuEl = document.querySelector(".row-dropdown-menu");
  const cell = menuEl?.closest(".action-cell-container");
  if (!menuEl || !cell) return;

  const clip = getClipBounds(menuEl);
  const anchor = cell.getBoundingClientRect();
  const h = menuEl.offsetHeight;
  const w = menuEl.offsetWidth;

  // Vertical: alinhado pelo topo da célula; sem espaço, alinha pelo rodapé.
  const roomBelow = clip.bottom - anchor.top;
  const roomAbove = anchor.bottom - clip.top;
  const up = h > roomBelow && roomAbove > roomBelow;
  const room = up ? roomAbove : roomBelow;

  // Horizontal: à direita da célula; sem espaço, espelha para a esquerda.
  const fitsRight = anchor.left + ROW_MENU_OFFSET + w <= clip.right;
  const fitsLeft = anchor.right - ROW_MENU_OFFSET - w >= clip.left;

  rowMenuPlacement.value = {
    up,
    left: !fitsRight && fitsLeft,
    // Não cabe em nenhum dos lados: limita e rola por dentro do menu.
    maxHeight: h > room ? Math.max(room, MENU_MIN_HEIGHT) : null,
    ready: true,
  };
}

function toggleRowDropdown(textId) {
  const willOpen = openDropdownId.value !== textId;
  openDropdownId.value = willOpen ? textId : null;
  if (!willOpen) return;

  rowMenuPlacement.value = {
    up: false,
    left: false,
    maxHeight: null,
    ready: false,
  };
  placeRowMenu();
}

function handleEdit(index) {
  indexSelector(index);
  // Edição comum: o foco é o de sempre (título), nunca o campo herdado de um
  // item em aberto clicado antes.
  editFocusField.value = null;
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  indexSelector(index);
  selectedTextCardId.value = textStore.idText;
  showTextCardModal.value = true;
  openDropdownId.value = null;
}

function handleViewPermissions(index) {
  indexSelector(index);
  selectedPermissionTextId.value = textStore.idText;
  showPermissionModal.value = true;
  openDropdownId.value = null;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function handleNotify(payload) {
  const type = payload?.type;
  const message = payload?.message || "";
  if (type === "success") showSuccessToast(message);
  else showErrorToast(message);
}

async function handleCardTextUpdated() {
  await handleTextsChanged({
    goToFirstPage: true,
    closeAdd: false,
    closeEdit: false,
    closeDelete: false,
    closeBatchDelete: false,
    clearSelection: true,
  });
}

function toggleSelection(id) {
  textStore.toggleSelection(id);
}

function tryOpenBatchDeleteModal() {
  if (!textStore.selectedTextIds || textStore.selectedTextIds.length === 0) {
    showErrorToast("Nenhum texto selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// Abrir um texto leva direto à tela de anotação: a série ("atividade") é
// resolvida por trás (última usada ou a padrão "Anotações gerais"). A tela de
// gestão de séries continua acessível pelo seletor no cabeçalho da anotação.
async function goToActivities(textId) {
  try {
    await activityStore.openTextForAnnotation(textId);

    router.push({ name: "RightArea" });
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403) {
      showErrorToast(
        error.response?.data?.message ||
          "Seu vínculo não permite anotar neste texto."
      );
    } else {
      showErrorToast(
        error.response?.data?.message ||
          "Não foi possível abrir o texto para anotação."
      );
    }
  }
}

async function fetchTextsPaged() {
  isLoading.value = true;
  try {
    await textStore.fetchTextsPaged(
      currentPage.value,
      pageSize.value,
      textStore.searchTerm
    );
  } catch (error) {
    console.error("❌ Erro ao buscar textos paginados:", error.message);
    showErrorToast("Falha ao carregar a lista de textos.");
  } finally {
    isLoading.value = false;
  }
}

watch(currentPage, fetchTextsPaged);

// A estante ABRE em "Meus textos": a tela é a biblioteca pessoal, e começar em
// "Todos" faz o acervo do Litterae — que é grande e igual para todo mundo —
// empurrar os textos do próprio usuário para as páginas seguintes.
//
// Escrito na store ANTES do `local` abaixo e ANTES dos watchers: o `local`
// nasce lendo daqui, e o watcher que dispara refetch captura este valor como
// estado inicial. Setar depois valeria uma busca a mais só para chegar ao
// mesmo lugar.
textStore.setMyTextsOnly(true);
// E limpa o criador: a store é compartilhada com a tela de tabela, e um
// `creatorName` sobrando de uma visita ao Acervo se somaria em AND a "meus
// textos" — a estante abriria vazia, sem nada na tela explicando por quê.
textStore.setFilter("creatorName", "");

// ─────────────────────────────────────────────────────────────────────────────
// FILTROS — mesmo padrão de NoteGeneralForm.vue / ActivityGeneralForm.vue
// ─────────────────────────────────────────────────────────────────────────────
const local = reactive({
  title: textStore.filters.title ?? "",
  authorName: textStore.filters.authorName ?? "",
  subtitle: textStore.filters.subtitle ?? "",
  source: textStore.filters.source ?? "",
  categoryName: textStore.filters.categoryName ?? "",
  topicName: textStore.filters.topicName ?? "",
  creatorName: textStore.filters.creatorName ?? "",
  creatorId: textStore.filters.creatorId ?? "",
  textId: textStore.filters.textId ?? "",
  createdFrom: textStore.filters.createdFrom ?? "",
  createdTo: textStore.filters.createdTo ?? "",
  permissionGroupName: textStore.filters.permissionGroupName ?? "",
  permissionUserName: textStore.filters.permissionUserName ?? "",
  // espelha myTextsOnly da store — vira chip como qualquer outra condição
  myTextsOnly: textStore.myTextsOnly ?? false,
});

const debouncedNumeric = debounce((field, value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed !== "" && (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0)) return;
  textStore.setFilter(field, trimmed);
}, 400);

const debouncedText = debounce((field, value) => {
  textStore.setFilter(field, String(value ?? "").trim());
}, 300);

watch(
  () => local.title,
  (v) => debouncedText("title", v)
);
watch(
  () => local.authorName,
  (v) => debouncedText("authorName", v)
);
watch(
  () => local.subtitle,
  (v) => debouncedText("subtitle", v)
);
watch(
  () => local.source,
  (v) => debouncedText("source", v)
);
watch(
  () => local.categoryName,
  (v) => debouncedText("categoryName", v)
);
watch(
  () => local.topicName,
  (v) => debouncedText("topicName", v)
);
watch(
  () => local.creatorName,
  (v) => debouncedText("creatorName", v)
);
watch(
  () => local.creatorId,
  (v) => debouncedNumeric("creatorId", v)
);
watch(
  () => local.textId,
  (v) => debouncedNumeric("textId", v)
);
watch(
  () => local.createdFrom,
  (v) => debouncedText("createdFrom", v)
);
watch(
  () => local.createdTo,
  (v) => debouncedText("createdTo", v)
);
watch(
  () => local.permissionGroupName,
  (v) => debouncedText("permissionGroupName", v)
);
watch(
  () => local.permissionUserName,
  (v) => debouncedText("permissionUserName", v)
);
watch(
  () => local.myTextsOnly,
  (v) => textStore.setMyTextsOnly(v)
);

watch(
  () => [{ ...textStore.filters }, textStore.myTextsOnly],
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchTextsPaged();
    }
  },
  { deep: true }
);

// `short` + `verb` compõem o rótulo da chip (ex.: Autor contém "Camões").
const ADVANCED_FILTERS = [
  {
    key: "subtitle",
    label: "Subtítulo",
    type: "text",
    placeholder: "ex: canto I…",
    short: "Subtítulo",
    verb: "contém",
    icon: "fas fa-align-left",
  },
  {
    key: "source",
    label: "Fonte",
    type: "text",
    placeholder: "ex: domínio público…",
    short: "Fonte",
    verb: "contém",
    icon: "fas fa-link",
  },
  {
    key: "creatorName",
    label: "Criador",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Criador",
    verb: "=",
    icon: "fas fa-user",
  },
  {
    key: "creatorId",
    label: "ID do criador",
    type: "numeric",
    placeholder: "ex: 7",
    short: "ID do criador",
    verb: "=",
    icon: "fas fa-id-badge",
  },
  {
    key: "createdRange",
    label: "Data de criação",
    type: "dateRange",
    fromKey: "createdFrom",
    toKey: "createdTo",
    short: "Data",
    icon: "fas fa-calendar-alt",
  },
  // Fecha a primeira coluna do menu "Adicionar filtro"
  {
    key: "categoryName",
    label: "Gênero",
    type: "text",
    placeholder: "ex: Épica…",
    short: "Gênero",
    verb: "contém",
    icon: "fas fa-book-open",
  },
  {
    key: "topicName",
    label: "Assunto",
    type: "text",
    placeholder: "ex: Tempos Verbais…",
    short: "Assunto",
    verb: "contém",
    icon: "fas fa-lightbulb",
  },
  {
    key: "textId",
    label: "ID do texto",
    type: "numeric",
    placeholder: "ex: 55",
    short: "ID do texto",
    verb: "=",
    icon: "fas fa-fingerprint",
  },
  {
    key: "permissionGroupName",
    label: "Compartilhado com grupo",
    type: "text",
    placeholder: "ex: Crianças…",
    short: "Compartilhado com grupo",
    verb: "quoted",
    icon: "fas fa-users-cog",
  },
  {
    key: "permissionUserName",
    label: "Compartilhado com usuário",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Compartilhado com usuário",
    verb: "quoted",
    icon: "fas fa-user-tag",
  },
];

// Filtros da linha principal — não entram no dropdown, mas viram chip no painel.
const BASE_FILTERS = [
  {
    key: "title",
    type: "text",
    short: "Título",
    verb: "contém",
    icon: "fas fa-heading",
  },
  {
    key: "authorName",
    type: "text",
    short: "Autor",
    verb: "contém",
    icon: "fas fa-feather-alt",
  },
  // Botão "Meus textos" — vira chip como qualquer outra condição.
  {
    key: "myTextsOnly",
    type: "checkbox",
    short: "Meus textos",
    icon: "fas fa-user",
  },
];
const ALL_FILTERS = [...BASE_FILTERS, ...ADVANCED_FILTERS];

const isAdvancedPanelOpen = ref(false);
const isAddFilterOpen = ref(false);
const addFilterMenuRef = ref(null);
const activeFilterKeys = ref([]);
const addedInThisSession = ref([]);

// Duas colunas independentes: abrir um campo cresce só a coluna dele.
const addFilterColumns = computed(() => {
  const options = addFilterOptions.value;
  const half = Math.ceil(options.length / 2);
  return [options.slice(0, half), options.slice(half)];
});

const activeFilters = computed(() =>
  activeFilterKeys.value
    .map((key) => ADVANCED_FILTERS.find((f) => f.key === key))
    .filter(Boolean)
);

// Filtros ainda não adicionados — é o que o dropdown mostra ao ser aberto.
const remainingFilters = computed(() =>
  ADVANCED_FILTERS.filter((f) => !activeFilterKeys.value.includes(f.key))
);

// Um filtro adicionado continua listado enquanto o dropdown permanecer aberto,
// para que o campo de preenchimento fique visível abaixo do seu nome.
const addFilterOptions = computed(() =>
  ADVANCED_FILTERS.filter(
    (f) =>
      !activeFilterKeys.value.includes(f.key) ||
      addedInThisSession.value.includes(f.key)
  )
);

function isFilterAdded(key) {
  return activeFilterKeys.value.includes(key);
}

// Um filtro só vira chip quando tem valor — opção clicada e não preenchida não conta.
function hasFilterValue(filter) {
  if (!filter) return false;
  if (filter.type === "checkbox") return local[filter.key] === true;
  if (filter.type === "dateRange")
    return Boolean(local[filter.fromKey] || local[filter.toKey]);
  return String(local[filter.key] ?? "").trim() !== "";
}

const chipFilters = computed(() => [
  ...BASE_FILTERS.filter(hasFilterValue),
  ...activeFilters.value.filter(hasFilterValue),
]);

// Filtros com controle próprio à vista (campos da linha principal e o botão
// "Meus textos"): sozinhos, não abrem o painel.
const PRIMARY_KEYS = ["title", "authorName", "myTextsOnly"];
const panelOnlyChips = computed(() =>
  chipFilters.value.filter((f) => !PRIMARY_KEYS.includes(f.key))
);

const isPanelVisible = computed(
  () => isAdvancedPanelOpen.value || panelOnlyChips.value.length > 0
);

// "Meus textos" tem o próprio botão para desligar — sozinho, não justifica o Limpar.
const hasActiveFilters = computed(() =>
  Object.entries(local).some(([key, v]) => {
    if (key === "myTextsOnly") return false;
    return typeof v === "boolean" ? v === true : String(v ?? "").trim() !== "";
  })
);

// Descarta as opções abertas no dropdown que ficaram sem preenchimento.
function pruneEmptyFilters() {
  activeFilterKeys.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  addedInThisSession.value = [];
}

function closeAddFilterDropdown() {
  isAddFilterOpen.value = false;
  pruneEmptyFilters();
}

function toggleAdvancedPanel() {
  isAdvancedPanelOpen.value = !isAdvancedPanelOpen.value;
  if (!isAdvancedPanelOpen.value) closeAddFilterDropdown();
}

// O menu pode abrir mesmo sem filtros restantes: opções com chip reabrem em edição.
const hasAddFilterContent = computed(
  () => remainingFilters.value.length > 0 || panelOnlyChips.value.length > 0
);

function toggleAddFilterDropdown() {
  if (isAddFilterOpen.value) {
    closeAddFilterDropdown();
    return;
  }
  if (!hasAddFilterContent.value) return;
  isAddFilterOpen.value = true;
  // Opções que já têm chip entram listadas e expandidas, exibindo seus valores.
  addedInThisSession.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  // Ao abrir, a primeira opção disponível já entra selecionada em modo de edição.
  const first = addFilterOptions.value[0];
  if (first) selectFilterOption(first.key);
}

// Seleção (via setas ou abertura do menu) já coloca o campo em modo de edição:
// expande a opção e foca o input, sem precisar de Enter. Checkbox não expande —
// marcar/desmarcar fica no Espaço para não ativar o filtro só de passagem.
function selectFilterOption(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def) return;
  if (def.type !== "checkbox" && !activeFilterKeys.value.includes(key)) {
    activeFilterKeys.value.push(key);
    addedInThisSession.value.push(key);
  }
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    if (!option) return;
    const target =
      def.type === "checkbox"
        ? option.querySelector(".add-filter-name")
        : option.querySelector('input:not([type="checkbox"])') ||
          option.querySelector(".add-filter-name");
    target?.focus();
  });
}

// Ao navegar para fora, opção expandida que ficou vazia volta a recolher.
function collapseFilterIfEmpty(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def || def.type === "checkbox" || hasFilterValue(def)) return;
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

// Setas movem a seleção entre as opções: cima/baixo percorrem a coluna (com
// volta); esquerda/direita alternam entre as colunas mantendo a linha.
// Em campos já preenchidos, ←/→ continuam movendo o cursor do input.
function handleAddFilterMenuKeydown(event) {
  // Tab não circula entre as opções — a navegação é pelas setas.
  if (event.key === "Tab") {
    event.preventDefault();
    return;
  }
  // Enter fecha o menu; os valores preenchidos viram chips.
  if (event.key === "Enter") {
    event.preventDefault();
    closeAddFilterDropdown();
    return;
  }
  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key))
    return;

  const optionEl = event.target.closest("[data-filter-key]");
  if (!optionEl) return;
  const currentKey = optionEl.dataset.filterKey;

  const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
  const isTextInput =
    event.target.matches("input") && event.target.type !== "checkbox";
  if (horizontal && isTextInput && event.target.value !== "") return;

  const columns = addFilterColumns.value;
  let col = columns.findIndex((c) => c.some((f) => f.key === currentKey));
  if (col === -1) return;
  let row = columns[col].findIndex((f) => f.key === currentKey);

  if (event.key === "ArrowDown") {
    row = (row + 1) % columns[col].length;
  } else if (event.key === "ArrowUp") {
    row = (row - 1 + columns[col].length) % columns[col].length;
  } else {
    const nextCol = (col + 1) % columns.length;
    if (!columns[nextCol]?.length) return;
    col = nextCol;
    row = Math.min(row, columns[col].length - 1);
  }

  event.preventDefault();
  collapseFilterIfEmpty(currentKey);
  selectFilterOption(columns[col][row].key);
}

function addFilter(key) {
  if (activeFilterKeys.value.includes(key)) return;
  activeFilterKeys.value.push(key);
  addedInThisSession.value.push(key);
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (def?.type === "checkbox") local[key] = true;
  focusAddedFilterInput(key);
}

// Ao adicionar um filtro pelo menu "Adicionar filtro", foca automaticamente o
// respectivo campo (após o v-if renderizar o input) para o usuário já digitar.
// Checkbox não tem campo de texto, então é ignorado.
function focusAddedFilterInput(key) {
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    const input = option?.querySelector('input:not([type="checkbox"])');
    if (input) input.focus();
  });
}

// Clique na opção: abre o campo; se já estiver aberto e sem preenchimento, recolhe.
function toggleFilterOption(key) {
  if (!activeFilterKeys.value.includes(key)) {
    addFilter(key);
    return;
  }
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!hasFilterValue(def)) removeFilter(key);
}

function removeFilter(key) {
  const def = ALL_FILTERS.find((f) => f.key === key);
  if (def?.type === "dateRange") {
    local[def.fromKey] = "";
    local[def.toKey] = "";
  } else if (def?.type === "checkbox") {
    local[key] = false;
  } else if (def) {
    local[key] = "";
  }
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

function clearAllFilters() {
  Object.keys(local).forEach((k) => {
    local[k] = typeof local[k] === "boolean" ? false : "";
  });
  activeFilterKeys.value = [];
  addedInThisSession.value = [];
  isAddFilterOpen.value = false;
  textStore.clearFilters();
}

// ── Coleções (abas) ──────────────────────────────────────────────────────────
// "Todos", "Meus textos" e "Acervo do Litterae" são recortes da MESMA consulta,
// não telas diferentes: por baixo continuam sendo os filtros `myTextsOnly` e
// `creatorName`.
//
// A aba ativa é COMPUTADA a partir dos filtros, e não um ref próprio. É o que
// impede os dois de discordarem: apagar a chip do criador no painel de
// ferramentas, ou clicar em "Limpar filtros", devolve a aba para "Todos"
// sozinho — com um ref, a barra continuaria acesa em "Acervo" mostrando a
// lista inteira.
// Os textos do Sys são o acervo que acompanha a aplicação. A aba é um atalho
// para o filtro `creatorName` que já existe.
//
// ⚠️ O match do backend é `Contains` (TextRepository), não igualdade: se um dia
// existir usuário com "sys" no nome (sysadmin, dbsys), os textos dele aparecem
// no acervo também. Consertar isso é mudança de contrato do filtro, no backend.
const SYS_CREATOR_NAME = "sys";

const isAcervoOnly = computed(
  () => (local.creatorName ?? "").trim().toLowerCase() === SYS_CREATOR_NAME
);

const activeCollection = computed(() => {
  if (local.myTextsOnly) return "mine";
  if (isAcervoOnly.value) return "acervo";
  return "all";
});

function selectCollection(key) {
  local.myTextsOnly = key === "mine";
  local.creatorName = key === "acervo" ? SYS_CREATOR_NAME : "";
  if (currentPage.value !== 1) currentPage.value = 1;
}

// Estante vazia SEM filtro nenhum não é "não achei": é "você ainda não
// começou". São dois estados diferentes e a tela precisa dizer coisas
// diferentes — "Nenhum texto encontrado" numa estante que nunca teve nada soa
// como falha de busca e deixa o usuário procurando o que não existe.
//
// `myTextsOnly` não entra na conta (nem em `hasActiveFilters`): a estante ABRE
// nele, então é o estado de repouso da tela, não uma escolha que o usuário fez
// e possa desfazer para "achar" alguma coisa.
const isFirstTextInvite = computed(
  () =>
    textStore.texts.length === 0 &&
    !isAcervoOnly.value &&
    !hasActiveFilters.value
);

// ── Painel de ferramentas e modo seleção ─────────────────────────────────────
// Filtros, busca e ações em lote saem da barra principal e passam a viver atrás
// de um botão só, no canto superior direito: em repouso, a tela é a estante.
const isToolsOpen = ref(false);

const scrollAreaRef = ref(null);

// O primeiro campo do painel. Abrir as ferramentas é quase sempre querer
// filtrar por título, então o cursor já entra nele.
const titleFilterRef = ref(null);

function toggleToolsPanel() {
  isToolsOpen.value = !isToolsOpen.value;
  if (!isToolsOpen.value) return;

  // O painel nasce no TOPO da área rolável, acima da grade. Com a página
  // rolada, abri-lo empurrava os cards para baixo e o usuário continuava vendo
  // o meio da lista — o painel que ele acabou de pedir ficava fora da tela.
  //
  // nextTick porque o v-show só solta a altura do painel depois do próximo
  // ciclo de render; rolar antes disso mira uma altura que ainda vai mudar.
  // É também quando o campo deixa de estar escondido - focar antes disso não
  // faz nada, porque o navegador não dá foco a elemento com display: none.
  nextTick(() => {
    scrollAreaRef.value?.scrollTo({ top: 0, behavior: "smooth" });
    titleFilterRef.value?.focus();
  });
}

// Marcar cards é gesto de administração, não de leitura — então as caixas de
// seleção só existem enquanto este modo está ligado. Desligar limpa o que
// estava marcado: sair do modo com seleção pendente deixaria "Apagar
// selecionados" agindo sobre itens que o usuário não vê mais marcados.
const isSelectionMode = ref(false);

function toggleSelectionMode() {
  isSelectionMode.value = !isSelectionMode.value;
  if (!isSelectionMode.value) {
    textStore.clearSelection();
    isBatchDropdownOpen.value = false;
  }
}

// Abrir as atividades era o clique na LINHA da tabela. No card, o clique abre os
// detalhes (é o que a estante promete: tocar na capa mostra o livro), então a
// entrada para as atividades passa a ser um item do menu ••• — sem isso, a
// função sumiria da tela.
function openActivitiesFromCard(index) {
  openDropdownId.value = null;
  handleRowClick(index);
}

// ── Chips do card ─────────────────────────────────────────────────────────────
// A composição das chips vem de utils/textCatalog.js: o painel de metadados do
// leitor (TextDisplayer.vue) mostra AS MESMAS chips do MESMO texto.
//
// O limite é daqui, e não de lá: ele existe para o card não crescer de altura de
// forma imprevisível e desalinhar a fileira — restrição desta tela. O excedente
// vira "+N", e a lista completa está nos detalhes.
const CARD_CHIP_LIMIT = 3;

function cardChips(text) {
  return catalogChips(text, { limit: CARD_CHIP_LIMIT });
}

// ── Sinopse exibida ──────────────────────────────────────────────────────────
// O texto vai INTEIRO para o DOM; quem corta é a altura da caixa, no CSS, que
// termina na base da capa. Corte por contagem de caracteres não serviria aqui:
// o número de caracteres que cabe depende da largura da coluna, então um limite
// fixo pararia antes da base numas telas e passaria dela noutras.
function synopsisText(text) {
  const value = (text?.synopsis ?? "").trim();
  return value || "Sinopse não informada";
}

// As reticências precisam saber se o texto TRANSBORDOU, e isso o CSS não
// enxerga: `::after` não tem como perguntar. `scrollHeight > clientHeight` tem,
// mas só existe no DOM. (O caminho nativo, `-webkit-line-clamp`, está fora:
// exige `display: -webkit-box`, que estabelece contexto próprio e faria o texto
// passar POR BAIXO do botão em vez de contorná-lo.)
function markClamped(el) {
  el.classList.toggle("is-clamped", el.scrollHeight - el.clientHeight > 1);
}

// `updated` também, e não só `mounted`: a mesma <div> é reaproveitada quando a
// página de cards troca, com sinopse de outro tamanho.
const vClamped = {
  mounted: markClamped,
  updated: markClamped,
};

// ── Capa do card ─────────────────────────────────────────────────────────────
// Texto sem imagem enviada ganha uma capa DESENHADA a partir dele mesmo. O
// gradiente e o tombo vêm de utils/textCover.js, e não daqui, porque o painel
// de metadados do leitor (TextDisplayer.vue) desenha a MESMA capa do MESMO
// texto — duas tabelas de paletas mantidas à mão acabariam divergindo.

// URL da capa enviada pelo usuário ("uploads/covers/<guid>.webp").
function coverUrl(text) {
  return mediaUrl(text?.coverPath);
}

// Avatar de quem criou o texto. Mesma conta da capa — o caminho é relativo ao
// wwwroot da API, e a foto do criador já vem na listagem
// (CreatedByUserPhotoPath), sem requisição extra.
function creatorAvatarUrl(text) {
  return mediaUrl(text?.createdByUserPhotoPath);
}

// O banco guarda caminho relativo; o arquivo é servido pelo wwwroot da API, que
// fica noutra origem que a do front. Daí montar a URL a partir do baseURL do
// axios, sem o sufixo /api — a mesma conta dos avatares nos cards de usuário.
function mediaUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = (api.defaults.baseURL || "")
    .replace(/\/api$/, "")
    .replace(/\/+$/, "");
  return `${base}/${path.replace(/^\/+/, "")}`;
}

function formatChipDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const [year, month, day] = raw.split("-");
  return day && month && year ? `${day}/${month}/${year.slice(-2)}` : raw;
}

// Rótulo da chip — curto e semântico. Os campos de preenchimento vivem no dropdown.
function chipLabel(filter) {
  if (filter.type === "checkbox") return filter.short;

  if (filter.type === "dateRange") {
    const from = formatChipDate(local[filter.fromKey]);
    const to = formatChipDate(local[filter.toKey]);
    if (from && to) return `${filter.short}: ${from} → ${to}`;
    if (from) return `${filter.short} ≥ ${from}`;
    if (to) return `${filter.short} ≤ ${to}`;
    return `${filter.short}…`;
  }

  const value = String(local[filter.key] ?? "").trim();
  if (!value) return `${filter.short}…`;
  // "quoted": o próprio rótulo já diz a relação (ex.: Compartilhado com grupo "Crianças")
  if (filter.verb === "quoted") return `${filter.short} "${value}"`;
  return filter.verb === "="
    ? `${filter.short} = ${value}`
    : `${filter.short} contém "${value}"`;
}

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }

  if (
    isAddFilterOpen.value &&
    !event.target.closest(".add-filter-dropdown-anchor")
  )
    closeAddFilterDropdown();

  // Foco saiu do botão "Mais filtros" e do painel sem nenhum filtro
  // efetivamente marcado → o botão volta ao estado desmarcado.
  if (
    isAdvancedPanelOpen.value &&
    !event.target.closest(".advanced-filters-anchor") &&
    !event.target.closest(".active-filters-panel") &&
    panelOnlyChips.value.length === 0
  ) {
    isAdvancedPanelOpen.value = false;
    closeAddFilterDropdown();
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  resetTextSearchState();
});

// Item em aberto clicado no fim do cadastro rápido: o texto já existe, e
// quem quer catalogá-lo agora segue direto para a edição dele. Se o clique veio
// de um item em aberto ("Autor", "Gênero", "Assuntos"), o campo vai junto para
// a edição abrir com o cursor nele.
const editFocusField = ref(null);

function closeEditModal() {
  showEditModal.value = false;
  editFocusField.value = null;
}

async function handleCompleteTextDetails(payload) {
  const textId = typeof payload === "object" ? payload?.textId : payload;
  editFocusField.value =
    (typeof payload === "object" ? payload?.focus : null) ?? null;

  showAddModal.value = false;
  textStore.setIdText(textId);

  await handleTextsChanged({
    goToFirstPage: true,
    closeAdd: true,
    closeEdit: false,
  });

  // O EditTextForm carrega o texto por conta própria (getTextById): aqui basta
  // deixar o id selecionado.
  showEditModal.value = true;
}

function indexSelector(index) {
  const text = textStore.texts[index];
  textStore.setIdText(text.textId);
  textStore.selectedText = text;
}

// ── Guard de atividades aplicado no clique da linha ──────────────────────────
// O clique da linha leva às atividades, então carrega os mesmos guards que
// antes ficavam no botão: o portão do vínculo (memberCanViewActivities) e a
// permissão por texto. Como uma linha não tem estado "desabilitado com
// tooltip", canOpenActivities também governa a classe .clickable-row — sem
// permissão, a linha nem exibe o cursor de mão, para o clique morto não
// parecer defeito.
function canOpenActivities(text) {
  if (!memberCanViewActivities.value) return false;
  return (
    (hasAnyPermission(text, "selecionar", "editar") && text?.active) ||
    userStore.currentUser?.userId == text?.createdByUserId
  );
}

function handleRowClick(index) {
  const text = textStore.texts[index];

  // Sem "selecionar"/"editar" o texto só pode ser lido na ficha. O clique
  // costumava morrer em silêncio, e quem não sabia da regra achava que era
  // defeito — daí o retorno explícito.
  if (!canOpenActivities(text)) {
    showErrorToast(
      "Você tem apenas permissão de visualizar este texto. Para anotar nele, " +
        "peça permissão de seleção ao responsável."
    );
    return;
  }

  indexSelector(index);
  goToActivities(text.textId);
}

// Sem equivalente ao getPrimaryTopicName do TextForm: a coluna estreita da
// tabela só comportava o assunto principal, e o card mostra todos em chips.

function hasAnyPermission(text, ...permissionNames) {
  if (!text || !Array.isArray(text.userPermissions)) return false;
  const lowerNames = permissionNames.map((p) => p.toLowerCase());
  return text.userPermissions.some((perm) =>
    lowerNames.includes(perm.permissionName?.toLowerCase())
  );
}

// ── NOVO: guards combinam vínculo (portão) AND grupo (filtro interno) ─────────
function canEdit(text) {
  return memberCanEditText.value && hasAnyPermission(text, "Editar");
}
function canView(text) {
  return memberCanViewTexts.value && hasAnyPermission(text, "Visualizar");
}
function canSelect(text) {
  return memberCanViewTexts.value && hasAnyPermission(text, "Selecionar");
}
function isTextCreator(text) {
  return userStore.currentUser?.userId === text?.createdByUserId;
}

async function handleStatusToggle(text) {
  try {
    await textStore.updateTextStatus({
      textId: text.textId,
      active: !text.active,
    });
    showSuccessToast(
      `Status do texto "${toTitleCase(text.title)}" alterado para ${
        text.active ? "inativo" : "ativo"
      } com sucesso!`
    );
  } catch (error) {
    console.error("❌ Falha ao atualizar o status do texto:", error);
    showErrorToast(
      error?.response?.data?.message ||
        error?.message ||
        "Ocorreu um erro ao atualizar o status."
    );
  }
}

async function handleTextsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;
  if (clearSelection) textStore.selectedTextIds = [];

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchTextsPaged();
    return;
  }
  await fetchTextsPaged();
}

// Sem o equivalente ao isSysUserName do TextForm: lá o "SYS" virava pílula numa
// coluna estreita; aqui o rodapé do card já escreve "Criado por <nome>", e uma
// pílula ao lado do nome repetiria a mesma informação em dois formatos.

onMounted(async () => {
  // Liga o loading já de cara — evita mostrar textStore.texts desatualizado
  // (de uma visita anterior) enquanto os fetches auxiliares abaixo ainda rodam.
  isLoading.value = true;
  await authorStore.fetchAuthors();
  await categoryStore.fetchCategories();
  await topicStore.fetchTopics();
  await fetchTextsPaged();
});
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header library-header">
        <!-- Tipografia pelas MESMAS classes do cabeçalho do TextForm
             (`text-xl font-bold` / `text-sm text-gray-700`), e não por regra
             própria: as duas telas são irmãs, e um tamanho escrito à mão aqui
             deixaria de acompanhar qualquer ajuste feito lá. -->
        <div class="library-heading">
          <h1 class="text-xl font-bold">Estante</h1>
          <div class="text-sm mt-1 text-gray-700 ">
            Sua biblioteca de textos organizados e sempre à mão.
            <!-- A ajuda mora na frase que apresenta a tela, como link: é ali
                 que quem chega pela primeira vez está lendo. Mesmas classes do
                 "Saiba mais" do TextForm (Pesquisa por Textos), para as duas
                 telas irmãs mostrarem o link igual. `<button>`, e não `<a>`,
                 porque abre um modal em vez de navegar. -->
            <button
              type="button"
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              title="Como esta tela funciona"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>

        <!-- Tudo que era barra de administração cabe aqui: filtros, busca,
             seleção múltipla e a ajuda. Em repouso, a tela é só a estante. -->
        <button
          type="button"
          class="tools-toggle"
          :class="{ active: isToolsOpen }"
          :title="isToolsOpen ? 'Ocultar ferramentas' : 'Filtros e ferramentas'"
          :aria-expanded="isToolsOpen"
          @click="toggleToolsPanel"
        >
          <i class="fas fa-sliders-h"></i>
          <span
            v-if="chipFilters.length"
            class="tools-toggle-count"
            :title="`${chipFilters.length} filtro(s) ativo(s)`"
            >{{ chipFilters.length }}</span
          >
        </button>
      </div>

      <!-- Coleções. São abas, não filtros soltos: a barra diz em qual acervo o
           usuário está navegando. Ver `activeCollection` no script. -->
      <div class="collection-tabs" role="tablist" aria-label="Coleções">
        <button
          type="button"
          role="tab"
          class="collection-tab"
          :class="{ active: activeCollection === 'all' }"
          :aria-selected="activeCollection === 'all'"
          @click="selectCollection('all')"
        >
          <i class="fas fa-layer-group"></i>
          <span>Todos</span>
        </button>

        <button
          type="button"
          role="tab"
          class="collection-tab"
          :class="{ active: activeCollection === 'mine' }"
          :aria-selected="activeCollection === 'mine'"
          @click="selectCollection('mine')"
        >
          <i class="fas fa-user"></i>
          <span>Meus textos</span>
        </button>

        <button
          type="button"
          role="tab"
          class="collection-tab"
          :class="{ active: activeCollection === 'acervo' }"
          :aria-selected="activeCollection === 'acervo'"
          @click="selectCollection('acervo')"
        >
          <i class="fas fa-book-open"></i>
          <span>Acervo do Litterae</span>
        </button>

      </div>

      <!-- Área rolável: cabeçalho e abas ficam fixos; ferramentas, cards e
           paginador rolam -->
      <div ref="scrollAreaRef" class="scroll-area">
        <div v-show="isToolsOpen" class="action-bar action-bar-stacked tools-panel">
        <div class="filter-panel w-full">
          <!-- Painel compartilhado pelos campos e pelos filtros ativos -->
          <div class="filter-fields-panel">
          <div class="filter-row">
            <!-- Título -->
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Título</label>
              <input
                ref="titleFilterRef"
                v-model="local.title"
                type="text"
                placeholder="ex: Dom Quixote…"
                class="filter-input"
              />
            </div>

            <!-- Autor -->
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Autor</label>
              <input
                v-model="local.authorName"
                type="text"
                placeholder="ex: Camões…"
                class="filter-input"
              />
            </div>

            <div class="filter-actions-right">
              <div
                class="filter-field filter-field-batch-actions advanced-filters-anchor"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  type="button"
                  class="btn btn-batch btn-advanced"
                  :class="{ active: isPanelVisible }"
                  title="Exibir mais filtros"
                  @click.stop="toggleAdvancedPanel"
                >
                  <i class="fas fa-filter"></i>
                  <span>Mais filtros</span>
                  <span v-if="chipFilters.length" class="advanced-count">{{
                    chipFilters.length
                  }}</span>
                  <svg
                    class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                    :class="{ open: isAdvancedPanelOpen }"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      clip-rule="evenodd"
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    />
                  </svg>
                </button>
              </div>

              <div
                v-if="hasActiveFilters"
                class="filter-field filter-field-clear"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  class="filter-clear"
                  title="Limpar filtros"
                  @click="clearAllFilters"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="clear-icon"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Limpar</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Filtros ativos no mesmo painel dos campos -->
          <div v-if="isPanelVisible" class="active-filters-panel">
            <div class="active-filters-title">
              <i class="fas fa-filter"></i>
              <span>Filtros ativos</span>
            </div>

            <div v-if="chipFilters.length" class="active-filters-chips">
              <div
                v-for="filter in chipFilters"
                :key="filter.key"
                class="filter-chip"
                :title="chipLabel(filter)"
              >
                <i :class="filter.icon" class="filter-chip-icon"></i>
                <span class="filter-chip-label">{{ chipLabel(filter) }}</span>
                <button
                  type="button"
                  class="filter-chip-remove"
                  :title="`Remover filtro ${filter.short}`"
                  @click="removeFilter(filter.key)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <p v-else class="active-filters-empty">Nenhum filtro adicionado.</p>

            <div
              class="add-filter-dropdown-anchor"
              style="position: relative; align-self: flex-start"
            >
              <button
                type="button"
                class="btn btn-batch btn-add-filter"
                :disabled="!hasAddFilterContent"
                @click.stop="toggleAddFilterDropdown"
              >
                <i class="fas fa-plus"></i>
                <span>Adicionar filtro</span>
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isAddFilterOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div
                v-if="isAddFilterOpen && addFilterOptions.length > 0"
                ref="addFilterMenuRef"
                class="dropdown-menu add-filter-menu"
                @keydown="handleAddFilterMenuKeydown"
              >
                <div class="add-filter-grid">
                  <div
                    v-for="(column, columnIndex) in addFilterColumns"
                    :key="`add-filter-col-${columnIndex}`"
                    class="add-filter-column"
                  >
                    <div
                      v-for="filter in column"
                      :key="filter.key"
                      :data-filter-key="filter.key"
                      class="add-filter-option"
                      :class="{
 'is-open': isFilterAdded(filter.key),
                        'is-checkbox': filter.type === 'checkbox',
                      }"
                      @click.stop="toggleFilterOption(filter.key)"
                    >
                      <button
                        type="button"
                        class="add-filter-name"
                        @click.stop="toggleFilterOption(filter.key)"
                      >
                        <i :class="filter.icon" class="add-filter-icon"></i>
                        <span>{{ filter.label }}</span>
                      </button>

                      <template v-if="isFilterAdded(filter.key)">
                        <template v-if="filter.type === 'dateRange'">
                          <div class="add-filter-inputs">
                            <span class="add-filter-sublabel">de</span>
                            <input
                              v-model="local[filter.fromKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                            <span class="add-filter-sublabel">até</span>
                            <input
                              v-model="local[filter.toKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                          </div>
                        </template>

                        <template v-else>
                          <input
                            v-model="local[filter.key]"
                            type="text"
                            :inputmode="
                              filter.type === 'numeric' ? 'numeric' : undefined
                            "
                            :placeholder="filter.placeholder"
                            class="add-filter-input"
                            @click.stop
                          />
                        </template>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          </div>

          <div class="filter-secondary-row">
            <!-- "Adicionar Texto" saiu daqui: virou o botão flutuante no canto
                 inferior direito. "Meus textos" e o acervo viraram abas. Sobra
                 nesta linha o que é de fato administração da lista. -->
            <button
              type="button"
              class="btn btn-batch btn-selection-mode"
              :class="{ active: isSelectionMode }"
              title="Marcar vários textos para ações em lote"
              @click="toggleSelectionMode"
            >
              <i class="fas fa-check-square"></i>
              <span>Selecionar vários</span>
            </button>

            <div v-show="isSelectionMode" ref="batchDropdownRef" class="relative">
              <button
                @click="isBatchDropdownOpen = !isBatchDropdownOpen"
                class="btn btn-batch"
                :disabled="!canBatchActions"
              >
                Ações em lote
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isBatchDropdownOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div v-if="isBatchDropdownOpen" class="dropdown-menu">
                <div class="py-1">
                  <!-- ── NOVO: Apagar em lote condicionado ao guard de delete ── -->
                  <a
                    v-if="memberCanDeleteText"
                    href="#"
                    role="button"
                    @click.prevent="tryOpenBatchDeleteModal"
                    class="dropdown-item danger-item"
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Apagar selecionados</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginador do topo: só os botões. Com 24 cards por página, a próxima
           página fica ao alcance sem rolar até o fim da grade. O paginador de
           baixo continua lá, com a contagem de registros — os dois leem o mesmo
           `currentPage`, então andam juntos.

           Mora aqui, e não na barra das coleções, para ficar sempre colado à
           grade: lá em cima, abrir as ferramentas enfiava o painel de filtros
           ENTRE ele e os cards, e ele deixava de anunciar a página do que está
           logo abaixo. -->
      <div v-if="!isLoading" class="cards-pagination-top">
        <!-- Quantos textos a coleção/filtro atual tem. É o TOTAL da consulta,
             não o tamanho da página: `totalCount` vem do backend já com os
             filtros aplicados, então o número acompanha aba, busca e filtros
             sem cálculo aqui. Fica à esquerda do paginador, que é de onde se lê
             "tantos textos, nestas páginas".

             A linha existe mesmo sem resultado nenhum: sem ela, "0 textos" - a
             resposta à busca que não achou nada - não teria onde aparecer. -->
        <span class="collection-count">
          {{ textStore.totalCount }}
          {{ textStore.totalCount === 1 ? "texto" : "textos" }}
        </span>

        <PaginationButtons
          v-if="textStore.totalCount > 0"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-count="textStore.totalCount"
          :total-pages="textStore.totalPages"
          @change-page="(page) => (currentPage = page)"
        />
      </div>

      <div class="table-wrapper">
        <!-- Selecionar a página inteira: só existe no modo seleção, junto com
             as caixas dos cards. Fora dele, nada de administração na estante. -->
        <div
          v-if="isSelectionMode && !isLoading && textStore.texts.length > 0"
          class="cards-toolbar"
        >
          <label class="cards-select-all">
            <input
              id="checkbox-all"
              type="checkbox"
              v-model="isAllSelectedOnPage"
              class="checkbox-style"
              :disabled="!anyEditableOnPage"
              :class="{ 'disabled-item': !anyEditableOnPage }"
            />
            <span>Selecionar todos desta página</span>
          </label>

          <span v-if="textStore.selectedTextIds?.length" class="cards-selected-count">
            {{ textStore.selectedTextIds.length }} selecionado{{
              textStore.selectedTextIds.length === 1 ? "" : "s"
            }}
          </span>
        </div>

        <div v-if="!isLoading && textStore.texts.length > 0" class="cards-grid">
          <article
            v-for="(text, index) in textStore.texts"
            :key="text.textId"
            class="text-card"
            :class="{
 'text-card--clickable': canOpenActivities(text),
              'text-card--menu-open': openDropdownId === text.textId,
              'text-card--selected': textStore.selectedTextIds?.includes(
                text.textId
              ),
            }"
            :title="
              canOpenActivities(text)
                ? null
                : 'Sem permissão para acessar as atividades deste texto'
            "
            @click="handleRowClick(index)"
          >
            <div class="card-top">
              <!-- Capa enviada pelo usuário. Já chega recortada em 3:4 pelo
                   utils/coverImage.js, então `object-fit: cover` aqui é rede
                   para capa antiga ou vinda por outro caminho. -->
              <img
                v-if="coverUrl(text)"
                class="card-cover card-cover--image"
                :src="coverUrl(text)"
                :alt="`Capa de ${text.title}`"
                loading="lazy"
                decoding="async"
              />

              <!-- Sem capa enviada: a desenhada, que sempre existe. Não há
                   estado vazio a projetar. -->
              <div
                v-else
                class="card-cover"
                :style="coverStyle(text)"
                aria-hidden="true"
              >
                <span class="cover-title">{{ toTitleCase(text.title) }}</span>
                <span v-if="text.authorName" class="cover-author">
                  {{ toTitleCase(text.authorName) }}
                </span>
                <span class="cover-tombo">{{ coverTombo(text) }}</span>
              </div>

              <input
                v-if="isSelectionMode"
                :class="{ 'disabled-item': !canEdit(text) }"
                type="checkbox"
                :value="text.textId"
                :checked="textStore.selectedTextIds?.includes(text.textId)"
                @change="toggleSelection(text.textId)"
                @click.stop
                class="checkbox-style card-checkbox"
                :disabled="!canEdit(text)"
                :title="
                  canEdit(text)
                    ? 'Selecionar este texto'
                    : 'Sem permissão para alterar este texto'
                "
              />

              <!-- A classe action-cell-container não é decorativa: é por ela que
                   placeRowMenu ancora o menu. Trocar o nome aqui faz o menu
                   abrir no canto errado, sem erro nenhum. -->
              <div @click.stop class="action-cell-container card-actions">
                <button
                  @click="toggleRowDropdown(text.textId)"
                  class="card-menu-btn"
                  title="Menu de ações"
                  aria-label="Menu de ações"
                >
                  <i class="fas fa-ellipsis-h"></i>
                </button>

                <div
                  v-if="openDropdownId === text.textId"
                  class="row-dropdown-menu"
                  :class="{
 'opens-up': rowMenuPlacement.up,
                    'opens-left': rowMenuPlacement.left,
                  }"
                  :style="{
                    maxHeight: rowMenuPlacement.maxHeight
                      ? rowMenuPlacement.maxHeight + 'px'
                      : null,
                    visibility: rowMenuPlacement.ready ? 'visible' : 'hidden',
                  }"
                >
                  <ul>
                    <li
                      :class="{
 'disabled-item': !(canView(text) || canSelect(text)),
                      }"
                      @click="
                        (canView(text) || canSelect(text)) && handleView(index)
                      "
                    >
                      <i class="fas fa-info-circle"></i
                      ><span>Detalhes do texto</span>
                    </li>

                    <!-- Antes era o clique na linha da tabela; no card o clique
                         abre os detalhes, então as atividades passam a ter
                         entrada própria aqui. -->
                    <li
                      :class="{ 'disabled-item': !canOpenActivities(text) }"
                      @click="
                        canOpenActivities(text) && openActivitiesFromCard(index)
                      "
                    >
                      <i class="fas fa-tasks"></i><span>Abrir atividades</span>
                    </li>

                    <li
                      v-if="isTextCreator(text)"
                      @click="handleViewPermissions(index)"
                    >
                      <i class="fas fa-user-shield"></i
                      ><span>Ver permissões</span>
                    </li>

                    <li
                      v-if="memberCanEditText"
                      :class="{ 'disabled-item': !canEdit(text) }"
                      @click="canEdit(text) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li
                      v-if="memberCanDeleteText"
                      :class="{
 'disabled-item': !canEdit(text),
                        'danger-item': true,
                      }"
                      @click="canEdit(text) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>

                  <div class="dropdown-divider"></div>

                  <div class="dropdown-toggle-item">
                    <span class="label-text">Ativo</span>
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        :checked="text.active"
                        :disabled="!canEdit(text)"
                        @change="handleStatusToggle(text)"
                      />
                      <span class="slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Sinopse. O ESPAÇADOR flutuante lá dentro é o que faz o texto
                   contornar o botão: o menu de verdade não pode morar aqui,
                   porque esta caixa corta o que passa da altura e cortaria o
                   dropdown junto. Ver o bloco .card-synopsis no CSS. -->
              <div
                v-clamped
                class="card-synopsis"
                :class="{ 'card-synopsis--empty': !text.synopsis }"
                :title="text.synopsis || 'Sinopse ainda não informada'"
              >
                <span class="card-synopsis__spacer" aria-hidden="true"></span>
                {{ synopsisText(text) }}
              </div>
            </div>

            <div class="card-body">
              <h3 class="card-title" :title="text.title">
                {{ toTitleCase(text.title) }}
              </h3>

              <!-- Autor é o segundo nome da lombada; sem ele, o travessão diz
                   "ainda não catalogado" em vez de parecer falha de carga. -->
              <p
                class="card-author"
                :class="{ 'card-author--empty': !text.authorName }"
                :title="text.authorName || 'Autor ainda não informado'"
              >
                {{ catalogLabel(text.authorName, "Autor") }}
              </p>

              <div class="card-chips">
                <span
                  v-for="(chip, cIdx) in cardChips(text).visible"
                  :key="`${text.textId}-chip-${cIdx}`"
                  class="card-chip"
                  :class="`card-chip--${chip.kind}`"
                  :title="
                    chip.kind === 'empty'
                      ? chip.name
                      : `${chip.kind === 'genre' ? 'Gênero' : 'Assunto'}: ${
                          chip.name
                        }`
                  "
                >
                  <!-- O rótulo de ausência não passa por toTitleCase: ele é
                       frase, não nome próprio catalogado. -->
                  {{ chip.kind === "empty" ? chip.name : toTitleCase(chip.name) }}
                </span>

                <span
                  v-if="cardChips(text).extra"
                  class="card-chip card-chip--more"
                  title="Abra os detalhes para ver todos"
                >
                  +{{ cardChips(text).extra }}
                </span>
              </div>

              <div class="card-foot">
                <!-- Avatar do criador; o ícone genérico só sobra para quem não
                     tem foto. -->
                <span class="card-foot-avatar">
                  <img
                    v-if="creatorAvatarUrl(text)"
                    :src="creatorAvatarUrl(text)"
                    :alt="`Avatar de ${text.createdByUserName}`"
                    loading="lazy"
                    decoding="async"
                  />
                  <i v-else class="far fa-user" aria-hidden="true"></i>
                </span>
                <span class="card-foot-label">Criado por</span>
                <span
                  class="card-foot-name"
                  :title="text.createdByUserName || 'Criador não informado'"
                >
                  {{ text.createdByUserName || "—" }}
                </span>
              </div>
            </div>
          </article>
        </div>

        <div v-if="isLoading" class="cards-state">
          <div class="empty-state-content">
            <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <p>Carregando textos...</p>
          </div>
        </div>

        <div v-else-if="textStore.texts.length === 0" class="cards-state">
          <div class="empty-state-content">
            <template v-if="isAcervoOnly">
              <i class="fas fa-book-open empty-icon"></i>
              <p>Nenhum texto no acervo do Litterae por aqui.</p>
              <p class="empty-hint">
                O acervo que acompanha o Litterae não trouxe resultados para
                estes filtros.
              </p>
              <button
                type="button"
                class="empty-link"
                @click="selectCollection('mine')"
              >
                Voltar para meus textos
              </button>
            </template>

            <template v-else-if="isFirstTextInvite">
              <img
                src="/img/estante-vazia.webp"
                alt=""
                width="320"
                height="205"
                class="empty-illustration"
              />
              <p class="empty-title">Sua estante ainda está vazia</p>
              <template v-if="memberCanCreateText">
                <p class="empty-hint">
                  Adicione seu primeiro texto para começar a criar séries de
                  anotações a partir dele.
                </p>
                <button
                  type="button"
                  class="empty-cta"
                  @click="showAddModal = true"
                >
                  <i class="fas fa-plus" aria-hidden="true"></i>
                  Adicionar meu primeiro texto
                </button>
              </template>
              
              <button
                type="button"
                class="empty-link"
                @click="selectCollection('acervo')"
              >
                Explorar o Acervo Litterae
              </button>
            </template>

            <template v-else>
              <img
                src="/img/estante-sem-resultado.webp"
                alt=""
                width="320"
                height="206"
                class="empty-illustration"
              />
              <p>Nenhum texto corresponde aos filtros.</p>
              <p class="empty-hint">
                Ajuste os termos da busca ou limpe os filtros para ver a
                estante inteira.
              </p>
              <button
                type="button"
                class="empty-link"
                @click="clearAllFilters"
              >
                Limpar filtros
              </button>
            </template>
          </div>
        </div>

        <div
          v-if="!isLoading && textStore.totalCount > 0"
          class="text-pagination-row"
        >
          <div class="text-pagination-left">
            <span>Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="textStore.totalCount"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="textStore.totalCount"
            :total-pages="textStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
        </div>
        <!-- fim table-wrapper -->
      </div>
      <!-- fim scroll-area -->

      <!-- Criar texto: flutuante no canto inferior direito, fora da barra
           principal. Fica sempre ao alcance sem disputar espaço com a estante.
           É `absolute` e não `fixed` porque o container é o quadro da tela —
           `fixed` o colaria na viewport, por cima de modais e do rodapé.

           Some no convite da estante vazia: ali o "Adicionar meu primeiro
           texto" é a MESMA ação, dita por extenso e no meio da tela. Manter os
           dois deixaria dois botões roxos competindo sobre uma tela sem nada
           para flutuar por cima. Ele volta assim que existe um texto — que é
           quando o acesso permanente passa a valer alguma coisa.

           A condição é `isFirstTextInvite`, não "lista vazia": nos vazios de
           filtro e de acervo o FAB continua, porque lá não há CTA nenhum. E ela
           é estável durante o carregamento, já que a store só troca `texts`
           quando a resposta chega — o FAB não pisca a cada refetch. -->
      <button
        v-if="memberCanCreateText && !isFirstTextInvite"
        type="button"
        class="fab-add"
        title="Adicionar texto"
        aria-label="Adicionar texto"
        @click="showAddModal = true"
      >
        <i class="fas fa-plus"></i>
      </button>
    </div>
  </div>

  <!-- Modais -->
  <Teleport to="body">
    <!-- ── NOVO: guards de vínculo nos modais ─────────────────────────────── -->
    <AddTextForm
      v-if="showAddModal && memberCanCreateText"
      @close="showAddModal = false"
      @text-added="handleTextsChanged({ goToFirstPage: true, closeAdd: true })"
      @complete-details="handleCompleteTextDetails"
    />

    <EditTextForm
      v-if="showEditModal && memberCanEditText"
      :textId="textStore.idText"
      :focusField="editFocusField"
      @close="closeEditModal"
      @text-updated="
        handleTextsChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <DeleteTextForm
      v-if="showDeleteModal && memberCanDeleteText"
      :text="textStore.selectedText"
      @close="showDeleteModal = false"
      @notify="handleNotify"
      @text-deleted="
        handleTextsChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeleteTextForm
      v-if="showBatchDeleteModal && memberCanDeleteText"
      @close="showBatchDeleteModal = false"
      @notify="handleNotify"
      @texts-deleted="
        handleTextsChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />

    <TextCardModal
      v-if="showTextCardModal"
      :show="showTextCardModal"
      :text-id="selectedTextCardId"
      @close="showTextCardModal = false"
      @text-updated="handleCardTextUpdated"
      @notify="handleNotify"
    />

    <PermissionView
      v-if="showPermissionModal"
      :show="showPermissionModal"
      entity-type="text"
      :text-id="selectedPermissionTextId"
      title="Permissões do texto"
      @close="showPermissionModal = false"
    />

    <TextHelpModal :show="showHelpModal" @close="showHelpModal = false" />
  </Teleport>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.main-header {
  flex-shrink: 0;
}

/* Rola tudo abaixo do cabeçalho (que fica fixo): filtros, tabela e paginador */
.scroll-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

/* ── FILTROS — mesmo padrão de NoteGeneralForm.vue / ActivityGeneralForm.vue ── */
.action-bar-stacked {
  flex-direction: column !important;
  align-items: stretch !important;
}
.filter-panel {
  width: 100%;
  flex-shrink: 0;
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 90px;
  min-width: 80px;
  max-width: 160px;
}
/* Título e Autor crescem para preencher toda a largura livre da linha */
.filter-field-wide {
  flex: 1 1 0;
  min-width: 130px;
  max-width: none;
}
.filter-field-clear,
.filter-field-batch-actions {
  flex: 0 0 auto;
  min-width: unset;
  max-width: unset;
}
.filter-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-light);
  white-space: nowrap;
  padding-left: 2px;
}
.filter-input {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.55rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.8rem;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}
.filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
/* Mais filtros / Limpar ficam colados à direita do campo Autor */
.filter-actions-right {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 0 0 auto;
  min-width: max-content;
}
/* Adicionar Texto / Ações em lote — abaixo do painel, à direita */
.filter-secondary-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: auto;
  height: 32px;
  padding: 0 0.65rem;
  flex-shrink: 0;
  border: 1px solid var(--danger);
  border-radius: 10px;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
  white-space: nowrap;
}
/* mesmo comportamento de clique do botão "Mais filtros" (.btn-batch) */
.filter-clear:hover {
  background-color: var(--danger-bg);
}
.filter-clear:active {
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  transform: translateY(1px);
}
.clear-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}
.btn-advanced.active,
.btn-my-texts.active {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
  color: var(--text-on-brand);
}
.btn-advanced.active:hover,
.btn-my-texts.active:hover {
  background-color: var(--brand-light-hover);
}
.advanced-count {
  display: inline-grid;
  place-items: center;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 999px;
  background-color: var(--text-light);
  color: var(--surface);
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}
/* Painel dos campos e filtros ativos */
.filter-fields-panel {
  padding: 14px 18px 16px;
  border: 1px solid rgba(202, 138, 4, 0.22);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.32);
  box-shadow: 0 2px 8px rgba(120, 53, 15, 0.06);
}

.active-filters-panel {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 0.75rem;
}
.active-filters-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-dark);
}
.active-filters-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.active-filters-empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-light);
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.35rem 0 0.6rem;
  border: 1px solid #ded5ff;
  /* mesmo raio do botão "Mais filtros" (.btn-batch) */
  border-radius: 10px;
  background-color: #ece7ff;
  color: #4b3f86;
}
.filter-chip-icon {
  width: 13px;
  text-align: center;
  font-size: 0.72rem;
  color: inherit;
  opacity: 0.75;
  flex-shrink: 0;
}
.filter-chip-label {
  font-size: 0.76rem;
  font-weight: 600;
  color: inherit;
  white-space: nowrap;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: inherit;
  opacity: 0.65;
  font-size: 0.7rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}
.filter-chip-remove:hover {
  background-color: var(--danger-bg);
  color: var(--danger-text);
  opacity: 1;
}
.btn-add-filter {
  gap: 0.4rem;
}
.add-filter-menu {
  right: auto;
  left: 0;
  /* alto o bastante para caber a coluna inteira com campos abertos, sem rolagem */
  max-height: min(560px, calc(100vh - 160px));
  overflow-y: auto;
  padding: 0.4rem;
}
/* duas colunas independentes: abrir um campo cresce só a coluna dele */
.add-filter-grid {
  display: flex;
  align-items: stretch;
}
.add-filter-column {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 330px;
  flex: 0 0 330px;
}
.add-filter-column + .add-filter-column {
  border-left: 1px solid var(--border-color);
}
/* o campo abre sempre na linha de baixo, ocupando a largura da coluna */
.add-filter-option {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.3rem;
  padding: 0.25rem 0.7rem;
  border-radius: 8px;
  min-width: 0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.add-filter-option:hover {
  background-color: #f5f3ff;
}
/* foco (navegação por setas) espelha o hover na linha inteira */
.add-filter-option:focus-within {
  background-color: #f5f3ff;
}
.add-filter-name:focus {
  outline: none;
}
.add-filter-option.is-checkbox {
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}
.add-filter-name {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.4rem;
  border: none;
  background: transparent;
  padding: 0.2rem 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 6px;
}
.add-filter-icon {
  width: 14px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--text-subtle);
  flex-shrink: 0;
}
.add-filter-inputs {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
  min-width: 0;
}
.add-filter-sublabel {
  font-size: 0.7rem;
  color: var(--text-light);
}
/* todos os campos com a mesma largura: a da coluna */
.add-filter-input {
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  height: 26px;
  padding: 0 0.4rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.78rem;
  box-sizing: border-box;
}
.add-filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
/* o período divide a mesma largura entre as duas datas */
.add-filter-input-date {
  flex: 1 1 0;
  width: auto;
}

.btn-pastel-blue {
  background-color: var(--focus-border);
  color: #1c3d5a;
}
.btn-pastel-blue:hover {
  background-color: #82b4ff;
}
.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}
/* Ação de consulta (card de detalhes): neutra, para não competir com a ação
   primária da linha. O ícone fica em tom médio de propósito — o disco sólido do
   info-circle pesa muito mais que os três pontos do menu de ações, e um slate
   escuro fazia o botão de consulta gritar mais alto que o de comandos. */
.btn-slate {
  background-color: var(--neutral-200);
  color: var(--text-muted);
}
.btn-slate:hover {
  background-color: var(--neutral-400);
}

/* Espelhos da ancoragem, ligados por placeRowMenu quando falta espaço */
.row-dropdown-menu.opens-up {
  top: auto;
  bottom: 0;
}

.row-dropdown-menu.opens-left {
  left: auto;
  right: 42px;
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 6px 0;
}

.dropdown-toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.65rem;
  cursor: default;
}
.label-text {
  font-size: 0.8rem;
  color: var(--text-dark);
}
.row-dropdown-menu .toggle-switch {
  width: 38px;
  height: 20px;
}
.row-dropdown-menu .slider {
  border-radius: 999px;
}
.row-dropdown-menu .slider:before {
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
}
.row-dropdown-menu input:checked + .slider:before {
  transform: translateX(18px);
}

.table-wrapper {
  flex: 1 0 auto;
}

/* Alinhado à direita, como os botões do paginador de baixo, e com respiro só
   embaixo: o painel de ferramentas, quando aberto, já traz o seu. A contagem
   acompanha o paginador em vez de ficar no canto oposto da tela - os dois dizem
   a mesma coisa, quanto há e onde se está. */
.cards-pagination-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.85rem;
  padding: 0 1.5rem;
  margin-bottom: 0.6rem;
}

/* Na barra das coleções ela era empurrada para a direita por `margin-left:auto`;
   aqui quem posiciona é o flex da linha. */
.cards-pagination-top .collection-count {
  margin-left: 0;
}

.user-pill-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 100%;
  justify-content: center;
}
.user-name {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sys-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.12rem 0.4rem;
  width: auto;
  white-space: nowrap;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(226, 232, 240, 0.8);
  color: rgba(51, 65, 85, 0.95);
}
td > .sys-pill {
  display: inline-flex !important;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-200);
  transition: 0.4s;
  border-radius: 28px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: var(--surface);
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: var(--brand-light);
}
input:checked + .slider:before {
  transform: translateX(22px);
}
input:disabled + .slider {
  cursor: not-allowed;
  opacity: 0.5;
}

.action-bar input,
.action-bar .search-input,
.search-text-input input {
  font-size: 0.8rem !important;
}

/* Desabilitado por permissão de vínculo: mantém pointer-events para o title/tooltip funcionar */
.btn-icon-disabled {
  opacity: 0.4;
  cursor: not-allowed !important;
}
.btn-icon-disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}
@keyframes btn-add-flash {
  0%   { box-shadow: none; }
  35%  { box-shadow: 0 0 0 3px #fde047, 0 0 12px 4px rgba(253, 224, 71, 0.7); }
  65%  { box-shadow: 0 0 0 3px #fde047, 0 0 12px 4px rgba(253, 224, 71, 0.7); }
  100% { box-shadow: none; }
}
.btn-primary--flash {
  animation: btn-add-flash 2.8s ease-in-out infinite;
}

/* Sem `margin`: o espaço acima vem do ritmo canônico de .empty-state-content.
   44ch quebra a explicação em duas linhas em vez de três linhas estreitas
   embaixo do título. */
.empty-hint {
  font-size: 0.85rem;
  color: var(--text-muted);
  max-width: 44ch;
  line-height: 1.5;
  /* Linhas de comprimento parecido, sem uma sobra curta no fim. */
  text-wrap: balance;
}
.empty-hint strong {
  color: var(--text-brand);
}

/* O convite pede um título de verdade: a mesma fonte do parágrafo faria a
   frase principal disputar atenção com a explicação abaixo dela. */
.empty-state-content .empty-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-dark);
}

/* A ação mora DENTRO do estado vazio. O FAB no canto continua lá, mas quem
   nunca criou nada não sabe que ele é o caminho — o botão precisa estar onde
   os olhos já estão, lendo a mensagem. */
.empty-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.15rem;
  border: none;
  border-radius: 999px;
  background: #7c5cff;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(124, 92, 255, 0.32);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
}
.empty-cta:hover {
  background: #6d4df2;
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(124, 92, 255, 0.38);
}
.empty-cta:focus-visible,
.empty-link:focus-visible {
  outline: 2px solid #7c5cff;
  outline-offset: 2px;
}

/* Saída secundária: existe para quem NÃO quer criar nada agora. Discreta de
   propósito, para não competir com a ação principal. */
.empty-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.83rem;
  font-weight: 600;
  color: var(--text-brand);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.empty-link:hover {
  color: #5b3fa8;
}

.search-text-wider {
  max-width: 360px;
  flex-shrink: 0;
}
.text-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.87rem;
}
.text-pagination-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-state-content {
  width: 100%;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Estante — o que substitui a .main-table nesta tela.

   As regras da tabela continuam neste arquivo de propósito: `.table-wrapper`
   segue sendo o elemento que recorta o menu de ações (ver placeRowMenu), e o
   `.row-dropdown-menu`, a folha de filtros e os modais são reaproveitados tal
   como estão. Só a apresentação da LISTA muda.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Cabeçalho ─────────────────────────────────────────────────────────────── */
.library-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.library-heading {
  min-width: 0;
}

/* Botão único de ferramentas: discreto em repouso, aceso quando o painel está
   aberto. O contador repete o número de filtros ativos porque, com o painel
   fechado, ele é a ÚNICA pista de que a lista está recortada. */
.tools-toggle {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--bg-content);
  color: var(--text-light);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease;
}

.tools-toggle:hover {
  background: var(--surface-3);
  color: var(--text-dark);
}

.tools-toggle.active {
  background: var(--brand-soft);
  border-color: #c4b5fd;
  color: var(--text-on-brand-soft);
}

.tools-toggle-count {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 17px;
  text-align: center;
}

/* ── Abas de coleção ───────────────────────────────────────────────────────── */
.collection-tabs {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem 0.5rem;
}

/* Contagem + paginador encostam na direita: a linha é flex, e a margem
   automática à esquerda da contagem come todo o espaço livre entre a última aba
   e esse par. Os dois somem juntos durante o carregamento, então a margem nunca
   fica órfã empurrando um elemento sozinho. */
.collection-count {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
}

.collection-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--bg-content);
  color: var(--text-light);
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease;
}

.collection-tab:hover {
  background: var(--surface-2);
}

/* A aba ativa é preenchida, não sublinhada: a barra fica sobre a grade de
   capas, e um sublinhado fino se perderia no meio das cores das capas. */
.collection-tab.active {
  background: var(--brand-soft);
  border-color: #c4b5fd;
  color: var(--text-on-brand-soft);
}

/* Painel de ferramentas: o antigo cabeçalho de administração, agora recolhido.
   v-show e não v-if — os filtros guardam estado local e reconstruí-los a cada
   abertura perderia o que estava digitado. */
/* Sem traço embaixo: quem separa as ferramentas dos cards agora é a moldura dos
   próprios blocos de filtro, e o filete por baixo do "Saiba mais" só somava
   uma linha a mais no caminho do olho. */
.tools-panel {
  margin-bottom: 0.5rem;
}

.btn-selection-mode.active {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
  color: var(--text-on-brand);
}

/* ── Grade ─────────────────────────────────────────────────────────────────── */
.cards-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0.1rem 0.6rem;
}

.cards-select-all {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  cursor: pointer;
  user-select: none;
}

.cards-selected-count {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-on-brand-soft);
}

.cards-grid {
  display: grid;
  /* Três colunas fixas: com pageSize = 9, a página é sempre a grade 3x3.
     `minmax(0, 1fr)` e não `1fr` porque o padrão de uma coluna de grade é
     `min-content` — sem o zero, um título longo dentro do card impediria a
     coluna de encolher e a grade estouraria a largura do painel. */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding-bottom: 0.5rem;
  align-items: stretch;
}

/* ── Card ──────────────────────────────────────────────────────────────────── */
.text-card {
  /* Medidas da capa num lugar só: a sinopse usa a MESMA altura como teto, e é
     isso que faz o texto terminar na base da capa. Escritas em variável para
     não poderem divergir — dois números iguais mantidos à mão sempre divergem. */
  --cover-w: 116px;
  --cover-h: calc(var(--cover-w) * 4 / 3);

  /* Respiro do card. Vale para o padding E para o vão entre a capa e a sinopse
     (o `gap` do .card-top): o texto fica tão distante da capa quanto a capa
     fica da borda do card. */
  --card-pad: 0.85rem;

  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: var(--card-pad);
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.15s ease, transform 0.15s ease,
    border-color 0.15s ease;
}

/* Mão e relevo só onde o clique leva a algum lugar — mesma regra da
   .clickable-row da tabela. Card sem permissão de atividades fica inerte, e o
   title explica por quê; prometer o clique e não cumprir é pior que não
   prometer. O menu ••• continua acessível nos dois casos. */
.text-card--clickable {
  cursor: pointer;
}

.text-card--clickable:hover {
  box-shadow: var(--shadow-card-hover);
  transform: translateY(-2px);
}

/* Card com o menu ••• aberto vai para cima dos vizinhos.

   Não é zelo: o `transform` do :hover acima CRIA UM CONTEXTO DE EMPILHAMENTO no
   card — e o card está sob o mouse justamente quando o menu está aberto. A
   partir daí, o `z-index: 30` do .row-dropdown-menu passa a valer só DENTRO do
   card, e o card, com z-index auto, perde para todo irmão que venha depois dele
   no DOM. O menu sumia sob o card da direita (nunca sob o da esquerda: esse
   pinta antes).

   Quem precisa subir é o CARD, não o menu: mexer no z-index do menu não o tira
   do contexto em que ele foi preso. */
.text-card--menu-open {
  z-index: 40;
}

.text-card--selected {
  border-color: var(--brand);
  box-shadow: 0 0 0 1px var(--brand);
}

.card-top {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  /* Mesmo valor do padding do card — ver --card-pad. */
  gap: var(--card-pad);
}

/* ── Capa ────────────────────────────────────────────────────────────────────
   Capa desenhada, para texto sem imagem enviada: o gradiente vem de
   coverStyle(), e o que se lê é o próprio texto. A proporção de livro é fixa para que a fileira inteira alinhe
   mesmo com títulos de tamanhos diferentes. */
.card-cover {
  position: relative;
  flex: 0 0 auto;
  /* Altura explícita, e não `aspect-ratio`: a sinopse precisa do MESMO valor
     como teto, e proporção não é um número que outra regra possa reutilizar. */
  width: var(--cover-w);
  height: var(--cover-h);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.7rem 0.55rem;
  border-radius: 8px;
  color: #fff;
  text-align: center;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
}

/* A capa enviada usa a mesma caixa da desenhada — mesma largura e proporção,
   para a fileira alinhar independentemente de qual das duas cada card mostra.
   `object-fit: cover` preenche sem deformar. */
.card-cover--image {
  display: block;
  padding: 0;
  object-fit: cover;
  background: var(--neutral-200);
}

/* Vinheta: dá profundidade sem precisar de uma segunda cor por paleta. */
.card-cover:not(.card-cover--image)::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at 50% 15%,
    rgba(255, 255, 255, 0.18),
    transparent 65%
  );
  pointer-events: none;
}

.cover-title {
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.25;
  /* Título longo é cortado em três linhas: a capa é reconhecimento visual, e o
     título completo está logo abaixo, no corpo do card. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.cover-author {
  font-size: 0.55rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* Tombo: encostado na base, como etiqueta de acervo. */
.cover-tombo {
  margin-top: auto;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  opacity: 0.75;
}

/* Caixa de seleção: só aparece no modo seleção, encostada na capa. */
.card-checkbox {
  position: absolute;
  top: 0.35rem;
  left: 0.35rem;
  z-index: 2;
}

/* Sinopse. A altura é limitada pelo número de linhas e não por `height`: assim
   ela cresce até o tamanho da capa e para, e a fileira inteira continua
   alinhada mesmo com sinopses de tamanhos diferentes. O texto completo fica no
   `title` e nos detalhes. */
/* Sinopse — a caixa que corta o texto.
   `overflow: hidden` a torna um contexto de formatação de bloco, e um BFC NÃO
   contorna float externo: ele se estreita para evitá-lo. Foi assim que a
   primeira tentativa falhou. A saída é o float morar DENTRO desta caixa (um
   BFC contorna os próprios floats) — daí o espaçador. */
.card-synopsis {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 0.72rem;
  /* 1.49 não é gosto, é aritmética: a capa tem 116px de largura em 3:4, logo
     154.67px de altura, e 154.67 ÷ 9 = 17.19px de linha — que sobre 11.52px de
     corpo (0.72rem) dá 1.492. Assim NOVE linhas fecham exatamente na base da
     capa. Com o 1.4 de antes cabiam 9,6 linhas: ou sobrava um vão de 10px, ou
     aparecia meia linha decepada na borda do corte.

     ⚠️ Mexer na largura da capa, no corpo da fonte ou nesta entrelinha desfaz a
     conta e traz de volta a fração de linha. Os três andam juntos. */
  line-height: 1.492;
  color: var(--text-muted);
  /* Justificado: a coluna é estreita e a sinopse ocupa quase toda a altura da
     capa, então a margem direita irregular ficava evidente ao lado da borda
     reta da capa. `hyphens: auto` acompanha a justificação — sem hifenização,
     coluna estreita justificada abre buracos entre as palavras. */
  text-align: justify;
  -webkit-hyphens: auto;
  hyphens: auto;
  /* Termina na BASE DA CAPA: o teto é a altura dela, e o que passa disso é
     cortado.

     `align-self: stretch` sozinho não servia — ele faz o item acompanhar a
     altura da fileira, mas quem define essa altura é o item mais alto, e com
     texto longo o mais alto passava a ser o próprio texto. Stretch estica, não
     limita; quem limita é o `max-height`. */
  align-self: stretch;
  max-height: var(--cover-h);
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* Reticências, só quando o texto de fato transbordou — a classe vem da diretiva
   v-clamped. Fundo sólido da cor do card para cobrir o pedaço de palavra que
   ficaria sob elas; sem fundo, o "…" se sobreporia às letras. */
.card-synopsis.is-clamped::after {
  content: "…";
  position: absolute;
  right: 0;
  bottom: 0;
  padding-left: 0.35em;
  background: var(--surface);
  pointer-events: none;
}

/* O espaçador reserva, DENTRO da caixa do texto, o lugar onde o botão aparece
   por cima. É ele que flutua — o menu não pode, porque seria cortado por este
   mesmo `overflow: hidden`.

   Só as duas primeiras linhas são encurtadas (a altura é a do botão); da
   terceira em diante o texto vai até a borda, alinhado com o título e os chips
   de baixo. */
.card-synopsis__spacer {
  float: right;
  width: 2.2rem;
  height: 1.9rem;
}

/* Mesma linguagem das demais ausências do card: itálico e apagado. */
/* Ausência centralizada nos dois eixos.

   `align-self: stretch` é o que dá altura para centralizar na vertical: o
   `.card-top` alinha os itens pelo topo, então sem isto a caixa teria a altura
   de uma linha de texto e não haveria vão nenhum onde centralizar. Esticada,
   ela acompanha a altura da capa, que é o item mais alto da fileira. */
.card-synopsis--empty {
  font-style: italic;
  color: var(--text-subtle);
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Sobrepõe o `justify` do estado normal, que numa frase curta e centralizada
     não faz sentido. */
  text-align: center;
}

/* O espaçador do botão vira ITEM DE FLEX quando a caixa acima passa a
   `display: flex`, e ocuparia 2.2rem à esquerda do texto — a frase deixaria de
   estar no centro. Some: sem texto a contornar, ele não tem função aqui. */
.card-synopsis--empty .card-synopsis__spacer {
  display: none;
}

/* O menu ancora aqui — a classe action-cell-container é lida pelo
   placeRowMenu, e o position: relative é o que a torna o bloco de contenção,
   como era a célula da tabela. */
/* Posicionado sobre o espaçador da sinopse, e FORA dela: dentro, o
   `overflow: hidden` da sinopse cortaria o menu suspenso. Ancorado no
   `.card-top`, que é `position: relative`.

   Continua sendo o bloco de contenção do menu (`position` explícito) e
   continua se chamando `action-cell-container`, que é como o placeRowMenu o
   encontra. */
.card-actions {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

.card-menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-subtle);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.card-menu-btn:hover {
  background: var(--surface-3);
  color: var(--text-dark);
}

/* ── Corpo ─────────────────────────────────────────────────────────────────── */
.card-body {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
  margin-top: 0.7rem;
  /* `flex: 1` é o que faz o rodapé colar embaixo.
     Os cards de uma fileira esticam para a altura do mais alto; sem isto, o
     corpo ficava do tamanho do próprio conteúdo e a sobra ia parar DEPOIS dele,
     no fim do card — o `margin-top: auto` do rodapé não tinha espaço para
     empurrar, e "Criado por" parava no meio do card (foi o que se viu no texto
     268, de título curto numa fileira com card alto). Com o corpo ocupando a
     sobra, a margem automática funciona. */
  flex: 1 1 auto;
}

.card-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-dark);
  line-height: 1.25;
  /* Duas linhas: um título longo não pode empurrar o rodapé e desalinhar a
     fileira inteira. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.card-author {
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mesma linguagem da chip de ausência: itálico e apagado, para não competir
   com o nome de um autor de verdade na fileira ao lado. */
.card-author--empty {
  font-style: italic;
  color: var(--text-subtle);
}

.card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.15rem;
}

.card-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.63rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Gênero e assunto são categorias diferentes e por isso têm cores diferentes:
   sem a distinção, uma fileira de chips iguais esconde qual é a classificação
   ampla e qual é o detalhe. */
.card-chip--genre {
  background: var(--brand-soft);
  color: var(--text-on-brand-soft);
}

.card-chip--topic {
  background: #e0f2fe;
  color: #0369a1;
}

.card-chip--more {
  background: var(--surface-3);
  color: var(--text-muted);
}

/* Ausência: contorno tracejado e sem preenchimento, para ler como lacuna e não
   como mais uma classificação ao lado das de verdade. */
.card-chip--empty {
  background: transparent;
  border: 1px dashed var(--neutral-400);
  color: var(--text-subtle);
  font-weight: 500;
  font-style: italic;
}

.card-foot {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: auto;
  padding-top: 0.6rem;
  font-size: 0.68rem;
  color: var(--text-subtle);
  min-width: 0;
}

/* Avatar do criador. Tamanho fixo e `flex: 0 0 auto` para não encolher quando o
   nome é longo — avatar deformado lê como defeito. */
.card-foot-avatar {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: var(--neutral-200);
  color: var(--text-muted);
  font-size: 0.6rem;
  overflow: hidden;
}

.card-foot-avatar img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}

.card-foot-label {
  white-space: nowrap;
}

.card-foot-name {
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Carregando / vazio: a tabela usava uma <tr> com colspan; aqui é um bloco
   próprio, com o mesmo conteúdo de .empty-state-content. */
.cards-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

/* ── Botão flutuante de criar ──────────────────────────────────────────────── */
.fab-add {
  position: absolute;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 999px;
  background: #7c5cff;
  color: #fff;
  font-size: 1.15rem;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(124, 92, 255, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.fab-add:hover {
  background: #6d4df2;
  transform: translateY(-2px);
  box-shadow: 0 14px 26px rgba(124, 92, 255, 0.45);
}

.fab-add:active {
  transform: translateY(0);
}

/* Houve aqui um `.fab-add--flash` — o FAB piscando enquanto a estante estava
   vazia. Saiu junto com a chegada do botão "Adicionar meu primeiro texto":
   eram dois chamarizes para a MESMA ação, um deles um loop infinito no canto
   oposto ao que o olho está lendo. O do estado vazio ganha porque diz em
   palavras o que acontece ao clicar. */
</style>
